import { AdministratorGallery, AdministratorGalleryController } from 'components/pages/Gallery/_main/AdministratorGallery/_main'
import { ClientProperty, ClientPropertyAITextGeneratorPage, ClientPropertyDocumentsPage, ClientPropertyInformationPage, ClientPropertyMarketingCampaignPage, ClientPropertyOrdersPage, ClientPropertyVisualsGalleryPage } from 'components/pages/Property/ClientProperty/_main'
import { CreativeGallery, CreativeGalleryController } from 'components/pages/Gallery/_main/CreativeGallery'
import { CreativeGuidelinesPage } from 'components/pages/Creative/CreativeGuidelines'
import { FC, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react'
import { Navigate, Route, matchPath, useLocation, useNavigate } from 'react-router-dom'
import { Profile, ProfilePageController, ProfileSettingsContent } from 'components/pages/Profile'
import { TestGallery, TestGalleryController } from 'components/pages/Gallery/TestGallery'
import { Workspace, WorkspaceInformationContent, WorkspaceIntegrationsContent, WorkspaceMembersContent, WorkspacePageController, WorkspacePlanContent, WorkspacePlaceholderContent } from 'components/pages/Workspace'

import AdminTools from 'components/pages/AdminTools'
import { AvailableAssignmentPage } from 'components/pages/Creative/AvailableAssignment'
import { ClientDashboard } from 'components/pages/Client/Dashboard'
import { ClientGallery } from 'components/pages/Gallery/_main/ClientGallery/_main'
import { ClientGalleryController } from 'components/pages/Gallery/_main/ClientGallery/_main/ClientGallery.controller'
import ClientOnboarding from 'components/pages/Administrator/ClientOnboarding/ClientOnboarding'
import { ClientPhotoStudio } from 'components/pages/PhotoStudioPhotoEditing/_main/ClientPhotoStudio/ClientPhotoStudio.page'
import { ClientPropertyExportingOptions } from 'components/pages/Property/ClientProperty/_main/pages/ClientPropertyExportingOptionsPage.page'
import { ClientPropertyStaging } from 'components/pages/Property/ClientProperty/_main/pages/ClientPropertyStagingPage.page'
import CreativeOnboarding from 'components/pages/Administrator/CreativeOnboarding/CreativeOnboarding'
import { CreativeUploadPage } from 'components/pages/CreativeUpload'
import { LightroomPluginGuidePage } from 'components/pages/Creative/LightroomPluginGuide'
import EmailVerified from 'components/pages/General/EmailVerified/EmailVerified'
import ErrorPage from 'components/pages/General/ErrorPage/ErrorPage'
import { EmbedGuard } from '../EmbedGuard/EmbedGuard'
import { EmbedSessionError } from 'components/pages/PurchaseFlow/_main/EmbedSessionError/EmbedSessionError'
import { EmbedStatusCard } from 'components/pages/EmbedStatusCard'
import { EmbedOnOfficeEntry } from 'components/pages/EmbedOnOffice'
import { FeatureFlag } from 'utils/featureFlags'
import { ForgotPassword } from 'components/pages/General/ForgotPassword'
import { GalleryStagingFlow } from 'components/pages/Gallery/pages/GalleryStagingFlow'
import { Index } from 'components/pages/Index/Index.component'
import { EmbedPurchaseFlowSwitch } from '../EmbedPurchaseFlowSwitch/EmbedPurchaseFlowSwitch'
import { LegacyWorkspacesProfileForward } from '../LegacyWorkspacesProfileForward/LegacyWorkspacesProfileForward'
import LoadingPage from 'components/pages/General/LoadingPage/LoadingPage'
import Login from 'components/pages/General/Login/Login'
import { NewProperty } from 'components/pages/NewProperty'
import NotFound from 'components/pages/General/NotFound/NotFound'
import NotLogged from 'components/pages/General/NotLogged/NotLogged'
import { Path, isEmbedRoute } from 'constants/router'
import { PaymentStatusPage } from 'components/pages/PaymentStatus'
import { PrivateRoute } from '../PrivateRoute/PrivateRoute'
import { PricingRedirect } from '../PricingRedirect/PricingRedirect'
import { PropertiesDashboard } from 'components/pages/PropertiesDashboard/_main'
import { FormerPurchaseFlowRevampRedirect, LegacyPurchaseFlowRoute, PurchaseFlowSwitch } from '../PurchaseFlowSwitch/PurchaseFlowSwitch'
import { SentryRoutes } from 'utils/analytics'
import { SignupPortal } from 'components/pages/General/SignupPortal'
import SignupRedirect from 'components/pages/General/SignupRedirect/SignupRedirect'
import { SignupRegistration } from 'components/pages/General/SignupRegistration'
import { SubscriptionFlowPage } from 'components/pages/SubscriptionFlow'
import { SubscriptionStatusPage } from 'components/pages/SubscriptionStatus'
import { TermsAndPrivacyPage } from 'components/pages/TermsAndPrivacy'
import UnverifiedUser from 'components/pages/General/UnverifiedUser/UnverifiedUser'
import { VisualsEditing } from 'components/pages/VisualsEditing'
import { VisualsMarketing } from 'components/pages/VisualsMarketing'
import { WorkspaceCreationPage } from 'components/pages/WorkspaceCreation/_main'
import { WorkspaceDeepLink } from '../WorkspaceDeepLink/WorkspaceDeepLink'
import { logger } from 'utils/logging'
import { useAppMeta } from '../App/appMeta.context'
import { useAuth0 } from 'utils/auth'
import { useFeatureFlag, useFeatureFlagsReadyFor, useFeatureFlagsStatus } from 'utils/featureFlags'
import { useFlagUserId } from 'utils/featureFlags/useFlagUserId'
import { embedSession } from 'utils/embedSession'

export const RouteSwitch: FC = () => {

  const { roles, isAuthenticated, user } = useAuth0()
  const location = useLocation()
  const navigate = useNavigate()
  const propstackIntegration = useFeatureFlag(FeatureFlag.PROPSTACK_INTEGRATION)
  // An onOffice session reaches the embedded purchase flow without the Propstack flag, which gates Propstack only.
  const isOnOfficeSession = useSyncExternalStore(embedSession.subscribe, embedSession.getProvider) === 'onoffice'
  const { flagsReady, flagsError } = useFeatureFlagsStatus()
  // v2 vs legacy may be targeted per user, so the anonymous evaluation is not the user's answer.
  const pfRevampFlagsReady = useFeatureFlagsReadyFor(useFlagUserId())

  const {
    isLoading,
    isUserVerified,
    isUserSet,
    isUserMeFailed,
  } = useAppMeta()

  const { pathname } = location

  /**
   * Redirects non-client users to the Orders dashboard page if they were redirect to the properties dashboard page after login.
   */
  useLayoutEffect(() => {
    if (!roles.isClient && pathname === Path.PROPERTIES_DASHBOARD) {
      navigate(Path.INDEX)
    }
  }, [roles, pathname, navigate])

  // matchPath (not pathname equality) so Propstack-controlled URLs with a trailing slash still count as embed
  const isEmbedPurchaseFlowPath = matchPath(Path.EMBED_PURCHASE_FLOW, pathname) !== null
  const isEmbedStatusCardPath = matchPath(Path.EMBED_STATUS_CARD, pathname) !== null

  // Diagnose an embed launch stuck behind the flag (silent 404 otherwise); waits for flags to resolve so the cold-load false default doesn't misfire
  const embedFlagOffLoggedRef = useRef(false)
  useEffect(() => {
    if (!isEmbedPurchaseFlowPath && !isEmbedStatusCardPath) return
    if (!flagsReady) return
    if (propstackIntegration || isOnOfficeSession) return
    if (embedFlagOffLoggedRef.current) return

    embedFlagOffLoggedRef.current = true
    logger.warn('Embed surface hit while propstack-integration flag is off', undefined, {
      context: { component: 'RouteSwitch', action: 'embedRouteFlagOff' },
    })
  }, [isEmbedPurchaseFlowPath, isEmbedStatusCardPath, flagsReady, propstackIntegration, isOnOfficeSession])

  if (isLoading) return <LoadingPage />
  if (!isUserVerified && pathname === Path.EMAIL_VERIFIED) return <EmailVerified />
  if (!isUserVerified) return <UnverifiedUser />
  // After the unverified screens: verifying re-asks /user/me, so an outage must not hide them
  // In the iframe ErrorPage's "go to /" would leave the embed; re-entering from the CRM is the only recovery.
  // Prefix match, not the exact matchPath values above: PF v2 steps live under the embed entry path.
  if (isUserMeFailed) return isEmbedRoute(pathname) ? <EmbedSessionError /> : <ErrorPage />
  if (!isUserSet) return <SignupRegistration />

  return (
    <SentryRoutes>

      <Route
        path={Path.INDEX}
        element={<Index />}
      />

      <Route
        path={Path.PROPERTIES_DASHBOARD}
        element={<Navigate to={Path.INDEX} replace />}
      />

      {roles.isCreative &&
        <Route
          path={Path.CT_UPLOAD}
          element={<PrivateRoute component={CreativeUploadPage} />
          }
        />
      }

      {roles.isCreative &&
        <Route
          path={Path.LIGHTROOM_PLUGIN_GUIDE}
          element={<PrivateRoute component={LightroomPluginGuidePage} />}
        />
      }

      {/* -- GALLERY CLUSTER -- */}
      {roles.isClient &&
        <>
          <Route
            path={Path.GALLERY_NO_SLUG}
            element={<ClientGallery />}
          >
            <Route
              path={Path.GALLERY}
              element={
                <PrivateRoute component={ClientGalleryController} />
              }
            />
            <Route
              path={Path.GALLERY_INFO}
              element={
                <PrivateRoute component={ClientGalleryController} />
              }
            />
            <Route
              path={Path.GALLERY_STAGING}
              element={<PrivateRoute component={GalleryStagingFlow} />}
            />
            {/* EXPORTING OPTIONS PAGE */}
            <Route
              path={Path.GALLERY_VISUALS_EDIT}
              element={
                <PrivateRoute component={VisualsEditing} />
              }
            />
            {/* PHOTO STUDIO PHOTO EDITING */}
            <Route
              path={Path.GALLERY_VISUAL_PHOTO_STUDIO}
              element={
                <PrivateRoute component={ClientPhotoStudio} />
              }
            />
            <Route
              path={Path.GALLERY_VISUALS_MARKETING}
              element={
                <PrivateRoute component={VisualsMarketing} />
              }
            />
          </Route>
        </>
      }

      {roles.isAdmin &&
        <>
          <Route
            path={Path.GALLERY_NO_SLUG}
            element={<AdministratorGallery />}
          >
            <Route
              path={Path.GALLERY}
              element={
                <PrivateRoute component={AdministratorGalleryController} />
              }
            />
          </Route>
        </>
      }

      {roles.isCreative &&
        <>
          <Route
            path={Path.GALLERY_NO_SLUG}
            element={<CreativeGallery />}
          >
            <Route
              path={Path.GALLERY}
              element={
                <PrivateRoute component={CreativeGalleryController} />
              }
            />
          </Route>
        </>
      }

      {roles.isAdmin &&
        <>
          <Route
            path={Path.GALLERY_NO_SLUG_TEST}
            element={<TestGallery />}
          >
            <Route
              path={Path.GALLERY_TEST}
              element={
                <PrivateRoute component={TestGalleryController} />
              }
            />
          </Route>
        </>
      }
      {/* -- END OF GALLERY CLUSTER -- */}

      {/* -- CLIENT PROPERTY CLUSTER -- */}
      {roles.isClient &&
        <>
          <Route
            path={Path.INDEX}
            element={
              <PrivateRoute component={PropertiesDashboard} />
            }
          />
          {/* PROPERTY EXPORTING OPTIONS PAGE */}
          <Route
            path={Path.PROPERTY_ASSIGNMENT_EXPORTING_OPTIONS}
            element={
              <PrivateRoute component={ClientPropertyExportingOptions} />
            }
          />
          {/* PHOTO STUDIO PHOTO EDITING */}
          <Route
            path={Path.PROPERTY_ASSIGNMENT_PHOTO_STUDIO}
            element={
              <PrivateRoute component={ClientPhotoStudio} />
            }
          />
          <Route
            path={Path.PROPERTY_ASSIGNMENT_STAGING}
            element={<PrivateRoute component={ClientPropertyStaging} />}
          />
          {/* NEW PROPERTY */}
          <Route
            path={Path.NEW_PROPERTIES}
            element={
              <PrivateRoute component={NewProperty} />
            }
          />
          <Route
            path={Path.PROPERTY_NO_SLUG}
            element={<PrivateRoute component={ClientProperty} />}
          >
            <Route
              path={Path.PROPERTY_ID}
            >
              <Route
                path={Path.PROPERTY_VISUALS_GALLERY}
                element={<PrivateRoute component={ClientPropertyVisualsGalleryPage} />}
              >
                <Route
                  path={Path.PROPERTY_VISUALS_GALLERY_ASSIGNMENT}
                  element={
                    <PrivateRoute component={ClientPropertyVisualsGalleryPage} />
                  }
                />
              </Route>
              <Route
                path={Path.PROPERTY_DOCUMENTS}
                element={<PrivateRoute component={ClientPropertyDocumentsPage} />}
              >
                <Route
                  path={Path.PROPERTY_DOCUMENTS_ASSIGNMENT}
                  element={
                    <PrivateRoute component={ClientPropertyDocumentsPage} />
                  }
                />
              </Route>
              <Route
                path={Path.PROPERTY_AI_TEXT_GENERATOR}
                element={<PrivateRoute component={ClientPropertyAITextGeneratorPage} />}
              />
              <Route
                path={Path.PROPERTY_MARKETING_CAMPAIGN}
                element={<PrivateRoute component={ClientPropertyMarketingCampaignPage} />}
              />
              <Route
                path={Path.PROPERTY_ORDERS}
                element={
                  <PrivateRoute component={ClientPropertyOrdersPage} />
                }
              />
              <Route
                path={Path.PROPERTY_INFO}
                element={
                  <PrivateRoute component={ClientPropertyInformationPage} />
                }
              />
            </Route>
          </Route>
        </>
      }
      {/* -- END OF PROPERTY CLUSTER -- */}

      {/* -- CLIENT ORDER CLUSTER -- */}
      {roles.isClient &&
        <>
          <Route
            path={Path.ORDERS}
            element={
              <PrivateRoute component={ClientDashboard} />
            }
          />
        </>
      }
      {/* -- END OF ORDER CLUSTER -- */}

      {/* -- SUBSCRIPTION FLOW CLUSTER -- */}
      {/* clients only */}
      <Route
        path={Path.SUBSCRIPTION}
        element={!isAuthenticated || roles.isClient
          ? <PrivateRoute component={SubscriptionFlowPage} />
          : <NotFound />
        }
      >
        <Route
          index
          element={<Navigate to={Path.SUBSCRIPTION_CREATION} />}
        />
        {/* The legacy subscription steps (deep links from bkbn.com/pricing) redirect to the workspace Plan page.
          * PricingRedirect, not an inline Navigate: the destination depends on the user's role in the
          * selected workspace, and resolving that needs a timer and an analytics event that must not run
          * on every route in the app. Deliberately not wrapped in PrivateRoute — a logged-out visitor must
          * keep reaching the NotLogged screen through the catch-all, not an Auth0 round trip (QA-246). */}
        <Route
          path={Path.SUBSCRIPTION_CREATION}
          element={<PricingRedirect />}
        />
        <Route
          path={Path.SUBSCRIPTION_MEMBERS}
          element={<PricingRedirect />}
        />
        <Route
          path={Path.SUBSCRIPTION_VALIDATION}
          element={<PricingRedirect />}
        />
        <Route
          path='*'
          element={
            <Navigate to={Path.SUBSCRIPTION} />
          }
        />
      </Route>
      {/* -- END OF SUBSCRIPTION FLOW CLUSTER -- */}
      {/* -- WORKSPACE CREATION (clients only) -- */}
      {roles.isClient &&
        <Route
          path={Path.WORKSPACE_CREATION}
          element={
            <PrivateRoute component={WorkspaceCreationPage} />
          }
        />
      }
      {/* -- PURCHASE FLOW CLUSTER -- */}
      {/* One path for the purchase flow: PF v2 while allow-pf-revamp is on, legacy while it is off. */}
      <Route
        path={`${Path.PURCHASE_FLOW}/*`}
        element={<PrivateRoute component={PurchaseFlowSwitch} />}
      />
      <Route
        path={Path.PURCHASE_FLOW_LEGACY}
        element={<PrivateRoute component={LegacyPurchaseFlowRoute} />}
      />
      <Route
        path={`${Path.PURCHASE_FLOW_LEGACY}/*`}
        element={<Navigate to={Path.PURCHASE_FLOW_LEGACY} />}
      />
      <Route
        path={`${Path.PURCHASE_FLOW_REVAMP_FORMER}/*`}
        element={<FormerPurchaseFlowRevampRedirect />}
      />
      {/* -- END OF PURCHASE FLOW CLUSTER -- */}
      {/* -- PROPSTACK EMBED PURCHASE FLOW (dark behind propstack-integration) -- */}
      {/* Mounted while flags still load so an iframe cold load never falls through to the wildcard route */}
      {/* `/*` so PF v2 can manage its own sub-routes here exactly as it does on its
          own path; legacy is sent back to the bare path it recognises as the embed. */}
      {(propstackIntegration || isOnOfficeSession || !pfRevampFlagsReady) &&
        <Route
          path={`${Path.EMBED_PURCHASE_FLOW}/*`}
          element={!pfRevampFlagsReady
            ? <LoadingPage />
            : <EmbedPurchaseFlowSwitch />
          }
        />
      }
      {/* -- END OF PROPSTACK EMBED PURCHASE FLOW -- */}
      {/* -- PROPSTACK EMBED STATUS CARD (dark behind propstack-integration) -- */}
      {(propstackIntegration || (!flagsReady && !flagsError)) &&
        <Route
          path={Path.EMBED_STATUS_CARD}
          element={!flagsReady && !flagsError
            ? <LoadingPage />
            : <EmbedGuard component={EmbedStatusCard} entryEvent='enters_embed_status_card' componentName='EmbedStatusCardGuard' />
          }
        />
      }
      {/* -- END OF PROPSTACK EMBED STATUS CARD -- */}
      {/* -- ONOFFICE EMBED ENTRY: opens the session, then hands over to the embedded purchase flow -- */}
      <Route path={Path.EMBED_ONOFFICE} element={<EmbedOnOfficeEntry />} />
      {/* -- PROFILE CLUSTER -- */}
      {/* WorkspaceDeepLink: emails link here with ?workspaceId= to open the settings on that workspace */}
      <Route
        path={Path.PROFILE}
        element={<WorkspaceDeepLink page="profile"><PrivateRoute component={Profile} /></WorkspaceDeepLink>}
      >
        <Route index element={<Navigate to={Path.PROFILE_SETTINGS} replace />} />
        <Route element={<PrivateRoute component={ProfilePageController} />}>
          <Route path={Path.PROFILE_SETTINGS} element={<PrivateRoute component={ProfileSettingsContent} />} />
        </Route>
      </Route>
      {/* -- END OF PROFILE CLUSTER -- */}
      {/* -- WORKSPACE SETTINGS CLUSTER -- */}
      {/* WorkspaceDeepLink: emails link to Members with ?workspaceId= to open the settings on that workspace.
          It wraps the whole cluster because Workspace and WorkspacePageController gate on the selected workspace. */}
      {roles.isClient &&
        <Route
          path={Path.WORKSPACE_SETTINGS}
          element={<WorkspaceDeepLink page="members"><PrivateRoute component={Workspace} /></WorkspaceDeepLink>}
        >
          <Route index element={<Navigate to={Path.WORKSPACE_SETTINGS_WORKSPACE} replace />} />
          <Route element={<PrivateRoute component={WorkspacePageController} />}>
            <Route path={Path.WORKSPACE_SETTINGS_WORKSPACE} element={<PrivateRoute component={WorkspaceInformationContent} />} />
            <Route path={Path.WORKSPACE_SETTINGS_MEMBERS} element={<PrivateRoute component={WorkspaceMembersContent} />} />
            <Route path={Path.WORKSPACE_SETTINGS_INTEGRATIONS} element={<PrivateRoute component={WorkspaceIntegrationsContent} />} />
            <Route path={Path.WORKSPACE_SETTINGS_PLAN} element={<PrivateRoute component={WorkspacePlanContent} />} />
            <Route path={Path.WORKSPACE_SETTINGS_TAB} element={<PrivateRoute component={WorkspacePlaceholderContent} />} />
          </Route>
        </Route>
      }
      {/* -- END OF WORKSPACE SETTINGS CLUSTER -- */}
      {/* Legacy single-workspace page: only forwards old email links to the Members settings as a ?workspaceId= deep link */}
      {roles.isClient &&
        <Route
          path={Path.WORKSPACES_PROFILE}
          element={
            <PrivateRoute component={LegacyWorkspacesProfileForward} />
          }
        />
      }
      <Route
        path="/API"
        element={
          <PrivateRoute component={AdminTools} />
        }
      />
      <Route
        path={Path.ADMIN_CLIENT_ONBOARDING}
        element={
          <PrivateRoute component={ClientOnboarding} />
        }
      />
      <Route
        path={Path.ADMIN_CREATIVE_ONBOARDING}
        element={
          <PrivateRoute component={CreativeOnboarding} />
        }
      />
      <Route
        path={Path.PAYMENT_STATUS}
        element={
          <PrivateRoute component={PaymentStatusPage} />
        }
      />
      <Route
        path={Path.SUBSCRIPTION_STATUS}
        element={
          <PrivateRoute component={SubscriptionStatusPage} />
        }
      />
      <Route
        path={Path.AVAILABLE_ASSIGNMENT}
        element={
          <PrivateRoute component={AvailableAssignmentPage} />
        }
      />
      {(roles.isCreative || roles.isAdmin) &&
        <>

          <Route
            path={Path.CREATIVE_GUIDELINES_PREVIEW}
            element={<PrivateRoute component={CreativeGuidelinesPage} />}
          />

          <Route
            path={Path.CREATIVE_GUIDELINES_FOR_KIND}
            element={<PrivateRoute component={CreativeGuidelinesPage} />}
          />

        </>
      }
      <Route
        path={Path.LOGIN}
        element={<Login />}
      />
      <Route
        path={Path.SIGNUP}
        element={<SignupRedirect />}
      />
      <Route
        path={Path.SIGNUP_PORTAL}
        element={<SignupPortal />}
      />
      <Route
        path={Path.EMAIL_VERIFIED}
        element={<EmailVerified />}
      />
      <Route
        path={Path.EMAIL_VERIFICATION}
        element={<UnverifiedUser />}
      />
      <Route
        path={Path.TERMS_AND_PRIVACY}
        element={<TermsAndPrivacyPage />}
      />
      <Route
        path={Path.FORGOT_PASSWORD}
        element={<ForgotPassword />}
      />
      <Route
        path="*"
        element={!isAuthenticated ?
          <NotLogged />
          :
          <NotFound />
        }
      />
    </SentryRoutes>
  )
}
